import React from 'react';

import { MailOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar } from 'antd';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';

import { Contact } from 'models/Contact';

import { useThemeLayoutConfig } from '../../../config/ThemeDashboardLayoutConfig';
import appI18n from '../../../config/i18n';

import './ContactCard.scss';

interface ContactCardProps {
  contact: Contact;
}

export const ContactCard: React.FC<ContactCardProps> = ({ contact }) => {
  const { theme, customValues } = useThemeLayoutConfig();
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const phone = `${contact.dialCode}${contact.phone}`;
  const whatsappLink = `https://wa.me/${phone.replace(/[^0-9]/g, '')}`;

  const styles = {
    card: {
      '--contact-primary-color': theme?.token?.colorPrimary,
      '--contact-secondary-color': customValues?.secondaryColor,
      '--contact-primary-hover': theme?.components?.Menu?.itemSelectedBg,
      '--contact-bg-container': theme?.token?.colorBgContainer,
      '--contact-bg-elevated': theme?.token?.colorBgElevated,
      '--contact-text-primary': theme?.token?.colorTextHeading,
      '--contact-text-secondary': theme?.token?.colorTextSecondary,
      '--contact-border-radius': `${(theme?.token?.borderRadiusLG || 0) * 2}px`,
      '--contact-border-radius-sm': `${theme?.token?.borderRadius}px`,
    } as React.CSSProperties,
  };

  const formatPhone = (phone: string) => {
    const phoneNumber = parsePhoneNumberFromString(phone);

    if (!phoneNumber || !phoneNumber.isValid()) {
      return phone; // retorna original si no es válido
    }

    return phoneNumber.formatInternational();
  };

  return (
    <div className="contact-card" style={styles.card}>
      <div className="contact-content">
        <div className="contact-avatar">
          <Avatar
            size={100}
            src={contact.avatarUrl}
            icon={<UserOutlined />}
            style={{ backgroundColor: 'white' }}
          />
        </div>
        <div className="contact-info">
          <div className="contact-header">
            <h3>{contact.fullName}</h3>
            <span className="position-badge">{contact.position}</span>
          </div>
          <div className="contact-actions">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-detail"
              title={t(
                'scenes.contact_us.components.contact_card.tooltips.send_whatsapp'
              )}
            >
              <PhoneOutlined className="icon" />
              {/*<WhatsAppOutlined className="icon" />*/}
              <span>{formatPhone(phone)}</span>
            </a>
            <a
              href={`mailto:${contact.email}`}
              className="contact-detail"
              title={t(
                'scenes.contact_us.components.contact_card.tooltips.send_email'
              )}
            >
              <MailOutlined className="icon" />
              <span>{contact.email}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
